import { orderBy } from "lodash";

/**
 * Replaces keys in an array of data objects based on a provided list.
 *
 * @param {Array} data - An array of data objects with keys to be replaced.
 * @param {Object} list - An object mapping original keys to replacement keys.
 * @return {Array} - An array of data objects with replaced keys.
 */
export const replaceKeys = (data, list) => {
  return data.map((d) => {
    const dataWithReplacedKeys = {
      key: list[d.key] || d.key,
      value: d.value,
    };
    return dataWithReplacedKeys;
  });
};

/**
 * Gets data for total_samples from an array of databases objects
 * and returns an array of data for the specified key.
 *
 * @param {Array} databases - An array of databases objects.
 * [
 *   {
 *     hasData: false,
 *     isChecked: true,
 *     label: "ΙΑRCTP53 Somatic",
 *     data: null,
 *   },
 *   {...},
 *   {
 *     hasData: false,
 *     isChecked: true,
 *     label: "ΙΑRCTP53 Somatic",
 *     data: {..., os_status: [{key: "Deceased", value: 24,}, {key: "Living", value: 16,}], ...},
 *   },
 * ];
 * @return {Array} - An array of data with total_samples for each db.
 * [
 *   { key: "ICGC Somatic", value: 2 },
 *   { key: "ΙΑRCTP53 Somatic", value: 30 },
 *   {...},
 *   { key: "cBioPortal", value: 61 },
 * ]
 */
export const getSamplesForEachDatabase = (databases) => {
  const samplesData = databases.map((db) => {
    if (db.data) {
      if (db.label === "ICGC Somatic") {
        return { key: db.label, value: db.data.affected_donors || 0 };
      }
      return {
        key: db.label,
        value: db.data.total_samples || 0,
      };
    }
    return { key: db.label, value: 0 };
  });
  return samplesData.filter((database) => database.value !== 0);
};

/**
 * Gets data for a specific key from an array of databases objects
 * and returns an array of data for the specified key.
 *
 * @param {Array} databases - An array of databases objects.
 * [
 *   {
 *     hasData: false,
 *     isChecked: true,
 *     label: "ΙΑRCTP53 Somatic",
 *     data: null,
 *   },
 *   {...},
 *   {
 *     hasData: false,
 *     isChecked: true,
 *     label: "ΙΑRCTP53 Somatic",
 *     data: {..., os_status: [{key: "Deceased", value: 24,}, {key: "Living", value: 16,}], ...},
 *   },
 * ];
 * @param {string} key - The specific key to extract data for.
 * "os_status"
 * @return {Array} - An array of data for the specified key.
 * [
 *   { key: "Deceased", value: 24 },
 *   { key: "Living", value: 16 },
 *   { key: "Living", value: 5 },
 * ];
 */
export const getDataFromAllDatabasesForSpecificKey = (databases, key) => {
  const filteredDbData = (
    databases?.filter((database) => database.hasData && database.isChecked) ??
    []
  ).map((db) => (db.data && db.data[key] ? db.data[key] : []));
  const aggregatedData = [].concat(...filteredDbData);
  const orderedData = orderBy(aggregatedData, (s) => s.value, ["desc"]);
  return orderedData ? [...orderedData] : [];
};

/**
 * Aggregates an array of key-value objects based on the 'key' property.
 *
 * @param {Array} dbsData - An array of key-value objects.
 * [
 *   { key: "Deceased", value: 24 },
 *   { key: "Living", value: 16 },
 *   { key: "Living", value: 5 },
 * ];
 * @return {Array} aggregatedData - An array of aggregated key-value objects.
 * [
 *   { key: "Deceased", value: 24 },
 *   { key: "Living", value: 21 },
 * ]
 */
export const getAggregatedDataForSpecificKey = (dbsData) => {
  if (!dbsData) return [];
  const aggregatedData = dbsData.reduce((result, databaseData) => {
    const existingItem = result.find((item) => item.key === databaseData.key);
    if (existingItem) {
      existingItem.value += databaseData.value;
    } else {
      result.push({ ...databaseData });
    }
    return result;
  }, []);
  return aggregatedData;
};

/**
 * Gets chart data for a specific key from an array of databases.
 *
 * @param {Array} databases - An array of databases objects.
 * [
 *   {
 *     hasData: false,
 *     isChecked: true,
 *     label: "ΙΑRCTP53 Somatic",
 *     data: null,
 *   },
 *   {...},
 *   {
 *     hasData: false,
 *     isChecked: true,
 *     label: "ΙΑRCTP53 Somatic",
 *     data: {..., os_status: [{key: "Deceased", value: 24,}, {key: "Living", value: 16,}], ...},
 *   },
 * ];
 * @param {string} key - The specific key to extract data for.
 * "os_status"
 * @param {boolean} replace - Indicates whether to replace keys in the data.
 * @param {Object} keyReplacements - An object mapping original keys to replacement keys.
 * @return {Array} - An array of aggregated data objects for the specified key.
 * [
 *   { key: "Deceased", value: 24 },
 *   { key: "Living", value: 21 },
 * ]
 */
export const getChartData = (databases, key, replace, keyReplacements) => {
  const rawData = getDataFromAllDatabasesForSpecificKey(databases, key);
  const transformedData = replace
    ? replaceKeys(rawData, keyReplacements)
    : rawData;
  const data = getAggregatedDataForSpecificKey(transformedData);
  return data;
};
